import { decode } from '@googlemaps/polyline-codec';
import { I18n } from 'aws-amplify/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ComputeRoutesResponse } from '@/components/pages/drive/details/type';
import type { Reservation, TimeInformation } from '@/features/driver/types/driveDetail';
import { RouteStatus } from '@/features/driver/types/driveDetail';
import { getTimelineData } from '@/features/driver/utils/processData';
import type { DropoffInformationType } from '@/features/map/types/dropoffInformationType';
import type { PickupDropoffInformationType } from '@/features/map/types/pickupDropoffInformationType';
import type { PickupInformationType } from '@/features/map/types/pickupInformationType';
import type { RoutesType } from '@/features/map/types/routesType';

const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string) || '';

type SegmentType = {
  id: number;
  path: { lat: number; lng: number }[];
  color: string;
  zindex: number;
};

type UseMapRoutesParams = {
  pickupInformation: PickupInformationType[];
  dropoffInformation: DropoffInformationType[];
  pickupDropoffInformation: PickupDropoffInformationType[];
  reservationList: Reservation[];
  storybookMode: boolean;
  onError: (message: string) => void;
};

type UseMapRoutesReturn = {
  routesData: RoutesType[];
  polylineFs: TimeInformation[];
  segments: SegmentType[];
  updateRoutesType: (newRouteType: RouteStatus[]) => void;
};

export const useMapRoutes = ({
  pickupInformation,
  dropoffInformation,
  pickupDropoffInformation,
  reservationList,
  storybookMode,
  onError,
}: UseMapRoutesParams): UseMapRoutesReturn => {
  const [routesData, setRoutesData] = useState<RoutesType[]>([]);
  const [polylineFs, setPolylineFs] = useState<TimeInformation[]>([]);
  const [segments, setSegments] = useState<SegmentType[]>([]);
  const [routeType, setRouteType] = useState<RouteStatus[]>([]);

  const updateRoutesType = useCallback((newRouteType: RouteStatus[]) => {
    setRouteType(newRouteType);
  }, []);

  // 乗降地点データからルート一覧を構築し、タイムラインデータを生成
  useEffect(() => {
    const combinedData = [
      ...pickupInformation.map(
        ({
          pickupWaypointId,
          latestEstimatedPickupDatetime,
          pickupWaypointSequence,
          pickupWaypointName,
          pickupWaypointPosition,
        }) => ({
          id: pickupWaypointId,
          datetime: latestEstimatedPickupDatetime,
          waypointSequence: pickupWaypointSequence,
          waypointName: pickupWaypointName,
          waypointPosition: pickupWaypointPosition,
          waypointFlag: 1,
        }),
      ),
      ...dropoffInformation.map(
        ({
          dropoffWaypointId,
          latestEstimatedDropoffDatetime,
          dropoffWaypointSequence,
          dropoffWaypointName,
          dropoffWaypointPosition,
        }) => ({
          id: dropoffWaypointId,
          datetime: latestEstimatedDropoffDatetime,
          waypointSequence: dropoffWaypointSequence,
          waypointName: dropoffWaypointName,
          waypointPosition: dropoffWaypointPosition,
          waypointFlag: 2,
        }),
      ),
      ...pickupDropoffInformation.map(
        ({
          pickupWaypointId,
          latestEstimatedPickupDatetime,
          pickupWaypointSequence,
          pickupWaypointName,
          pickupWaypointPosition,
        }) => ({
          id: pickupWaypointId,
          datetime: latestEstimatedPickupDatetime,
          waypointSequence: pickupWaypointSequence,
          waypointName: pickupWaypointName,
          waypointPosition: pickupWaypointPosition,
          waypointFlag: 3,
        }),
      ),
    ];

    const sortedData = combinedData.sort((a, b) => {
      const dateA = new Date(a.datetime).getTime();
      const dateB = new Date(b.datetime).getTime();
      if (dateA !== dateB) return dateA - dateB;

      const sequenceA = a.waypointSequence ? parseInt(a.waypointSequence, 10) : Infinity;
      const sequenceB = b.waypointSequence ? parseInt(b.waypointSequence, 10) : Infinity;

      return sequenceA - sequenceB;
    });

    setRoutesData(sortedData);

    if (reservationList) {
      setPolylineFs(getTimelineData(reservationList));
    }
  }, [dropoffInformation, pickupDropoffInformation, pickupInformation, reservationList]);

  const options: RequestInit | null = useMemo(() => {
    if (polylineFs?.length === 0) return null;

    return {
      method: 'post',
      body: JSON.stringify({
        origin: {
          location: {
            latLng: {
              latitude: polylineFs[0]?.latitude ?? null,
              longitude: polylineFs[0]?.longitude ?? null,
            },
          },
          vehicleStopover: true,
        },
        destination: {
          location: {
            latLng: {
              latitude: polylineFs[polylineFs.length - 1]?.latitude ?? null,
              longitude: polylineFs[polylineFs.length - 1]?.longitude ?? null,
            },
          },
          vehicleStopover: true,
        },
        intermediates:
          polylineFs.length > 0
            ? polylineFs.slice(1, -1).map((item) => ({
                location: {
                  latLng: { latitude: item.latitude, longitude: item.longitude },
                },
                vehicleStopover: true,
              }))
            : [],
        optimizeWaypointOrder: false,
        travelMode: 'DRIVE',
        routingPreference: 'TRAFFIC_AWARE',
        routeModifiers: { avoidTolls: true, avoidHighways: true, avoidFerries: true },
        computeAlternativeRoutes: false,
        languageCode: 'ja',
        units: 'METRIC',
      }),
    };
  }, [polylineFs]);

  const requestHeaders: HeadersInit = useMemo(
    () => ({
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': apiKey,
      'X-Goog-FieldMask': 'routes,routes.optimized_intermediate_waypoint_index',
    }),
    [],
  );

  // Google Routes APIを呼び出してポリラインセグメントを取得
  useEffect(() => {
    if (storybookMode || !options) return;

    const fetchGoogleRoutes = async () => {
      try {
        const response = await fetch(import.meta.env.VITE_GOOGLE_ROUTES_API_URL as string, {
          ...options,
          headers: requestHeaders,
        });
        const data = (await response.json()) as ComputeRoutesResponse;
        if (data?.routes?.length > 0) {
          const { legs } = data.routes[0];
          const newSegments: SegmentType[] = legs.flatMap((leg, index) => {
            const decodedPath = decode(leg.polyline.encodedPolyline, 5);

            return {
              id: index,
              path: decodedPath.map(([lat, lng]) => ({ lat, lng })),
              color: 'blue',
              zindex: 1,
            };
          });
          setSegments(newSegments);
        }
      } catch (error) {
        const err = error as { message: string };
        onError(I18n.get(err.message) as string);
      }
    };

    void fetchGoogleRoutes();
  }, [options, requestHeaders, storybookMode, onError]);

  // ルートステータスに応じてセグメントの色を更新
  useEffect(() => {
    if (routeType?.length === 0 || segments.length === 0) return;

    const colorMap: Record<RouteStatus, { color: string; zindex: number }> = {
      [RouteStatus.SCHEDULED]: { color: 'blue', zindex: 1 },
      [RouteStatus.RUNNING]: { color: 'red', zindex: 2 },
      [RouteStatus.FINISHED]: { color: 'gray', zindex: 1 },
    };

    const newSegments: SegmentType[] = segments.map((segment, index) => {
      const style = colorMap[routeType[index]] ?? { color: 'black', zindex: 1 };

      return { ...segment, ...style };
    });

    if (JSON.stringify(newSegments) !== JSON.stringify(segments)) {
      setSegments(newSegments);
    }
  }, [routeType, segments]);

  return { routesData, polylineFs, segments, updateRoutesType };
};
